import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { doc, getDoc, setDoc, collection, addDoc, updateDoc, arrayUnion } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { initiatePhonePeTransaction, checkPaymentStatus } from "../utils/phonepePayment";
import { processRazorpayPayment } from "../utils/razorpayPayment";
import SubscriptionPlansConfig from "../config/subscriptionPlansConfig";
import { Star } from "lucide-react";

import SelectMenu from "../components/crm/SelectMenu";
function Subscription() {
  const { globalUser } = useAuth();
  const navigate = useNavigate();
  const [subscriptionEndDate, setSubscriptionEndDate] = useState(null);
  const [daysLeft, setDaysLeft] = useState(0);
  const [hoursLeft, setHoursLeft] = useState(0);
  const [minutesLeft, setMinutesLeft] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [subscriptionType, setSubscriptionType] = useState("");
  const [userCategory, setUserCategory] = useState("");
  const [selectedPlan, setSelectedPlan] = useState(0); // 0 for 6 months, 1 for yearly
  const [showEnquiryModal, setShowEnquiryModal] = useState(false);
  const [enquiryForm, setEnquiryForm] = useState({
    name: "",
    representingFirm: "No",
    firmName: "",
    email: "",
    contactNo: "",
    requirements: "",
    approximateCases: "",
    maxCaseCourt: "",
  });
  const isClient = localStorage.getItem("userType") === "client";
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [enquiryLoading, setEnquiryLoading] = useState(false);
  const [selectedLawyerPlan, setSelectedLawyerPlan] = useState(null); // 'basic' or 'premium'
  const [showFreePlanModal, setShowFreePlanModal] = useState(false);
  const [activeSubscription, setActiveSubscription] = useState(null);
  const [activeSubscriptionStatus, setActiveSubscriptionStatus] = useState("");
  const [activeSubscriptionCategory, setActiveSubscriptionCategory] = useState(null);
  const [userData, setUserData] = useState(null);

  // Helper function to check if user is from India/Pakistan (INR pricing)
  const isINRCountry = () => {
    const countryCode = userData?.countryCode || "";
    return countryCode === "+91" || countryCode === "+92";
  };

  // Get the correct currency based on user's country
  const getUserCurrency = () => {
    return isINRCountry() ? "INR" : "USD";
  };

  useEffect(() => {
    fetchUserCategory();
    fetchSubscriptionDetails();
  }, [globalUser]);

  useEffect(() => {
    const timer = setInterval(() => {
      calculateTimeLeft();
    }, 1000);

    return () => clearInterval(timer);
  }, [subscriptionEndDate]);

  const fetchUserCategory = async () => {
    if (!globalUser) return;

    try {
      if (isClient) {
        const clientDocRef = doc(db, "client", globalUser.uid);
        const clientDoc = await getDoc(clientDocRef);
        if (clientDoc.exists()) {
          const data = clientDoc.data();
          setUserCategory("client");
          setUserData(data);
        }
      } else {
        const userDocRef = doc(db, "lawyers", globalUser.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const data = userDoc.data();
          setUserCategory(data.userCategory || data.userCateogry || "");
          setUserData(data);
        }
      }
    } catch (error) {
      console.error("Error fetching user category:", error);
    }
  };

  const fetchSubscriptionDetails = async () => {
    if (!globalUser) return;

    try {
      const subscriptionDocRef = doc(db, "subscriptions", globalUser.uid);
      const subscriptionDoc = await getDoc(subscriptionDocRef);

      if (subscriptionDoc.exists()) {
        const data = subscriptionDoc.data();
        if (data && data.transactions) {
          const transactions = data.transactions;
          let latestEndDate = null;
          let latestType = "";
          let activeTransaction = null;

          // Find the latest active subscription
          for (const transaction of transactions) {
            let endDate;
            if (transaction["end-time"]?.toDate) {
              endDate = transaction["end-time"].toDate();
            } else {
              endDate = new Date(transaction["end-time"]);
            }

            // Check if subscription is active
            if (endDate > new Date()) {
              if (!latestEndDate || endDate > latestEndDate) {
                latestEndDate = endDate;
                latestType = transaction.type || "free";
                activeTransaction = transaction;
              }
            }
          }

          if (latestEndDate) {
            setSubscriptionEndDate(latestEndDate);
            setSubscriptionType(latestType);
            setHasActiveSubscription(latestEndDate > new Date());

            // Set active subscription details
            if (activeTransaction && latestEndDate > new Date()) {
              setActiveSubscription(activeTransaction);
              setActiveSubscriptionCategory(activeTransaction.category);

              // Calculate days left
              const now = new Date();
              const difference = latestEndDate - now;
              const daysRemaining = Math.floor(difference / (1000 * 60 * 60 * 24));

              // Format status text
              const planName = formatPlanName(activeTransaction.category);
              setActiveSubscriptionStatus(
                `${planName} - ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} left`
              );
            }
          }
        }
      }
    } catch (error) {
      console.error("Error fetching subscription details:", error);
      toast.error("Failed to load subscription details");
    }
  };

  const formatPlanName = (category) => {
    if (!category) return "Free Plan";

    switch (category) {
      case "Lawyer-Basic":
        return "Lawyer-Basic Plan";
      case "Lawyer-Premium":
        return "Lawyer-Premium Plan";
      case "Lawyer-Enterprise":
        return "Lawyer-Enterprise Plan";
      default:
        return category;
    }
  };

  const formatDate = (dateValue) => {
    try {
      let date;
      if (dateValue?.toDate) {
        date = dateValue.toDate();
      } else if (dateValue instanceof Date) {
        date = dateValue;
      } else {
        return "N/A";
      }

      const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
                      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
      return `${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
    } catch (error) {
      console.error("Error formatting date:", error);
      return "N/A";
    }
  };

  const calculateTimeLeft = () => {
    if (!subscriptionEndDate) return;

    const now = new Date();
    if (subscriptionEndDate > now) {
      const difference = subscriptionEndDate - now;
      setDaysLeft(Math.floor(difference / (1000 * 60 * 60 * 24)));
      setHoursLeft(Math.floor((difference / (1000 * 60 * 60)) % 24));
      setMinutesLeft(Math.floor((difference / (1000 * 60)) % 60));
      setSecondsLeft(Math.floor((difference / 1000) % 60));
      setHasActiveSubscription(true);
    } else {
      setDaysLeft(0);
      setHoursLeft(0);
      setMinutesLeft(0);
      setSecondsLeft(0);
      setHasActiveSubscription(false);
    }
  };

  // Directly process payment based on user's country code
  const handleUpgradeClick = async (planType, amountINR, amountUSD, duration, category) => {
    if (paymentLoading) return;

    const currency = getUserCurrency();
    const amount = currency === "INR" ? amountINR : amountUSD;

    await handleRazorpayPayment(planType, amount, duration, category, currency);
  };

  // Handle Razorpay payment
  const handleRazorpayPayment = async (planType, amount, duration, category, currency = "INR") => {
    if (paymentLoading) return;

    try {
      setPaymentLoading(true);
      toast.info("Initiating payment...");

      const userEmail = userData?.email || globalUser?.email || "";
      const userName = userData?.name || userData?.fullName || "";
      const userPhone = userData?.mobileNumber || userData?.phoneNumber || "";

      const result = await processRazorpayPayment({
        amount,
        currency,
        userId: globalUser.uid,
        userEmail,
        userName,
        userPhone,
        planDetails: {
          planType,
          duration,
          category,
        },
      });

      if (result.success) {
        toast.success("Payment successful! Your subscription has been activated.");
        // Refresh subscription details
        await fetchSubscriptionDetails();
        // Navigate to dashboard after a short delay
        setTimeout(() => {
          navigate("/portal/Dashboard");
        }, 2000);
      } else {
        toast.error(result.error || "Payment failed. Please try again.");
      }
    } catch (error) {
      console.error("Razorpay payment error:", error);
      toast.error("Payment failed. Please try again.");
    } finally {
      setPaymentLoading(false);
    }
  };

  // Legacy PhonePe payment handler (kept for backwards compatibility)
  const handleUpgrade = async (planType, amount, duration, category) => {
    if (paymentLoading) return;

    try {
      setPaymentLoading(true);

      // Get user data
      const userDocRef = doc(db, "lawyers", globalUser.uid);
      const userDoc = await getDoc(userDocRef);

      if (!userDoc.exists()) {
        toast.error("User data not found");
        setPaymentLoading(false);
        return;
      }

      const userDataLocal = userDoc.data();
      const phoneNumber = userDataLocal.mobileNumber || userDataLocal.phoneNumber;

      if (!phoneNumber) {
        toast.error("Phone number not found. Please update your profile.");
        setPaymentLoading(false);
        return;
      }

      // Initiate PhonePe payment
      const paymentResponse = await initiatePhonePeTransaction(
        amount,
        phoneNumber,
        globalUser.uid
      );

      if (paymentResponse.success) {
        // Store payment details temporarily in localStorage for verification after redirect
        const paymentDetails = {
          merchantTransactionId: paymentResponse.merchantTransactionId,
          amount,
          duration,
          category,
          planType,
          userId: globalUser.uid,
          timestamp: new Date().toISOString(),
        };
        localStorage.setItem("pendingPayment", JSON.stringify(paymentDetails));

        // Redirect to PhonePe payment page
        window.location.href = paymentResponse.redirectUrl;
      } else {
        toast.error(paymentResponse.error || "Failed to initiate payment");
        setPaymentLoading(false);
      }
    } catch (error) {
      console.error("Payment error:", error);
      toast.error("Failed to initiate payment. Please try again.");
      setPaymentLoading(false);
    }
  };

  const handleLawStudentUpgrade = () => {
    const amountINR = selectedPlan === 0
      ? SubscriptionPlansConfig.LAW_STUDENT_SIX_MONTHS.price
      : SubscriptionPlansConfig.LAW_STUDENT_ONE_YEAR.price;
    // Law student plans use INR pricing for all countries (no USD conversion specified)
    const amountUSD = amountINR;
    const duration = selectedPlan === 0 ? 6 : 12;
    const planType = selectedPlan === 0 ? "6months" : "yearly";
    handleUpgradeClick(planType, amountINR, amountUSD, duration, "lawStudent");
  };

  const handleLawyerUpgrade = (plan) => {
    let amountINR, amountUSD, duration, category;

    if (plan === "basic") {
      amountINR = SubscriptionPlansConfig.LAWYER_BASIC.priceINR;
      amountUSD = SubscriptionPlansConfig.LAWYER_BASIC.priceUSD;
      duration = 1;
      category = SubscriptionPlansConfig.LAWYER_BASIC.category;
    } else if (plan === "premium") {
      amountINR = SubscriptionPlansConfig.LAWYER_PREMIUM.priceINR;
      amountUSD = SubscriptionPlansConfig.LAWYER_PREMIUM.priceUSD;
      duration = 1;
      category = SubscriptionPlansConfig.LAWYER_PREMIUM.category;
    }

    handleUpgradeClick(plan, amountINR, amountUSD, duration, category);
  };

  const handleEnquirySubmit = async (e) => {
    e.preventDefault();

    if (!enquiryForm.name || !enquiryForm.email || !enquiryForm.contactNo) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(enquiryForm.email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    // Phone must be exactly 10 digits
    if (enquiryForm.contactNo.length !== 10) {
      toast.error("Phone number must be exactly 10 digits");
      return;
    }

    setEnquiryLoading(true);
    try {
      // Save to Firebase
      await addDoc(collection(db, "enterpriseEnquiries"), {
        ...enquiryForm,
        userId: globalUser?.uid || "anonymous",
        timestamp: new Date(),
      });

      // Email notification to admin
      const clientFields = isClient ? `
Approximate number of cases: ${enquiryForm.approximateCases || "Not specified"}
Court with maximum cases: ${enquiryForm.maxCaseCourt || "Not specified"}` : "";

      const adminEmailParams = {
        name: enquiryForm.name,
        email: enquiryForm.email,
        phoneNumber: enquiryForm.contactNo,
        query: `${isClient ? "Client" : "Enterprise"} Enquiry

Representing Firm: ${enquiryForm.representingFirm}
${enquiryForm.representingFirm === "Yes" ? `Firm Name: ${enquiryForm.firmName || "Not provided"}` : ""}${clientFields}

Requirements:
${enquiryForm.requirements || "Not specified"}`,
      };

      // Thank you email to user
      const thankYouEmailParams = {
        name: enquiryForm.name,
        email: enquiryForm.email,
      };

      try {
        // Send both emails
        await Promise.all([
          axios.post("https://jurident-feedback-backend-596718606544.us-central1.run.app/sendQuery", adminEmailParams),
          axios.post("https://jurident-feedback-backend-596718606544.us-central1.run.app/send-enquiry-thank-you", thankYouEmailParams)
        ]);
        toast.success("Enquiry submitted successfully! Check your email for confirmation.");
      } catch (emailError) {
        console.error("Error sending email notification:", emailError);
        toast.success("Enquiry submitted successfully!");
      }

      setShowEnquiryModal(false);
      setEnquiryForm({
        name: "",
        representingFirm: "No",
        firmName: "",
        email: "",
        contactNo: "",
        requirements: "",
        approximateCases: "",
        maxCaseCourt: "",
      });
    } catch (error) {
      console.error("Error submitting enquiry:", error);
      toast.error("Failed to submit enquiry. Please try again.");
    } finally {
      setEnquiryLoading(false);
    }
  };

  

  const handleEnquiryChange = (e) => {
    const { name, value } = e.target;
    // Text-only fields: allow letters, spaces, dots, hyphens only
    const textOnlyFields = ["name", "firmName", "maxCaseCourt"];
    if (textOnlyFields.includes(name) && value && !/^[a-zA-Z\s.\-']+$/.test(value)) {
      return;
    }
    // Phone: digits only, max 10
    if (name === "contactNo") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setEnquiryForm((prev) => ({ ...prev, [name]: digitsOnly }));
      return;
    }
    setEnquiryForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  return (
    <>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
      />
      <Navbar />
      <div className="subscription-page min-h-screen bg-[#050125] relative overflow-hidden">
        <div className="background-circle circle-3 z-0"></div>
        <div className="background-circle circle-4 z-0"></div>

        {/* Header */}
        <div className="relative bg-transparent py-4 mb-6">
          <div className="max-w-6xl mx-auto px-4 flex items-center justify-between">
            <Link
              to="/portal/account"
              className="flex items-center text-white hover:text-yellow-500 transition"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </Link>
            <Link
              to="/portal/Dashboard"
              className="text-yellow-500 hover:text-yellow-400 transition"
            >
              <svg
                className="w-8 h-8"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                />
              </svg>
            </Link>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 py-8">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img
              src={`${process.env.PUBLIC_URL}/logo.png`}
              alt="Logo"
              className="h-20 object-contain"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          </div>

          {/* Title */}
          <h1 className="text-4xl font-bold text-blue-400 text-center mb-8">
            Subscription Plan
          </h1>

          {/* Countdown Timer for Free Subscription */}
          {hasActiveSubscription && subscriptionType === "free" && (
            <div className="bg-gray-700 border-2 border-white rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center justify-center flex-1">
                  <svg
                    className="w-8 h-8 text-white mr-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <h2 className="text-2xl font-semibold text-white">
                    Free Subscription Expires In
                  </h2>
                </div>
                <button
                  onClick={() => setShowFreePlanModal(true)}
                  className="text-white hover:text-yellow-500 transition ml-2"
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </button>
              </div>

              <div className="text-center">
                <div className="text-6xl font-bold text-white mb-2">
                  {daysLeft} {daysLeft === 1 ? "Day" : "Days"}
                </div>
                <div className="text-4xl font-medium text-white tracking-wider mb-2">
                  {String(hoursLeft).padStart(2, "0")}:
                  {String(minutesLeft).padStart(2, "0")}:
                  {String(secondsLeft).padStart(2, "0")}
                </div>
                <div className="text-sm text-gray-300 tracking-widest mb-6">
                  Hours&nbsp;&nbsp;&nbsp;&nbsp;Min&nbsp;&nbsp;&nbsp;&nbsp;Sec
                </div>

                {/* Dates for Free Plan */}
                {activeSubscription && (
                  <div className="flex justify-evenly items-center pt-4 border-t border-gray-500">
                    {/* Start Date */}
                    <div className="flex flex-col items-center">
                      <svg
                        className="w-5 h-5 text-white opacity-90 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                      <div className="text-xs text-white opacity-80 font-medium mb-1">
                        Start Date
                      </div>
                      <div className="text-sm text-white font-semibold">
                        {formatDate(activeSubscription["start-time"])}
                      </div>
                    </div>

                    {/* End Date */}
                    <div className="flex flex-col items-center">
                      <svg
                        className="w-5 h-5 text-white opacity-90 mb-2"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      <div className="text-xs text-white opacity-80 font-medium mb-1">
                        End Date
                      </div>
                      <div className="text-sm text-white font-semibold">
                        {formatDate(activeSubscription["end-time"])}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Active Subscription Status Card - Only show for paid plans */}
          {activeSubscription && subscriptionType !== "free" && (
            <div className="bg-gray-700 border-2 border-gray-500 rounded-2xl p-6 mb-8">
              <div className="flex items-center justify-center mb-4">
                <Star className="w-6 h-6 text-white mr-2" fill="white" />
                <h2 className="text-3xl font-bold text-white">
                  Active Subscription
                </h2>
              </div>

              <div className="text-center mb-6">
                <div className="text-2xl font-semibold text-white">
                  {activeSubscriptionStatus}
                </div>
              </div>

              <div className="flex justify-evenly items-center">
                {/* Start Date */}
                <div className="flex flex-col items-center">
                  <svg
                    className="w-5 h-5 text-white opacity-90 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <div className="text-xs text-white opacity-80 font-medium mb-1">
                    Start Date
                  </div>
                  <div className="text-sm text-white font-semibold">
                    {formatDate(activeSubscription["start-time"])}
                  </div>
                </div>

                {/* End Date */}
                <div className="flex flex-col items-center">
                  <svg
                    className="w-5 h-5 text-white opacity-90 mb-2"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  <div className="text-xs text-white opacity-80 font-medium mb-1">
                    End Date
                  </div>
                  <div className="text-sm text-white font-semibold">
                    {formatDate(activeSubscription["end-time"])}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* No User Category - Profile Incomplete (skip for clients with required fields filled) */}
          {!userCategory && globalUser && !isClient && (
            <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white mb-8 text-center">
              <svg
                className="w-16 h-16 text-yellow-500 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
              <h2 className="text-2xl font-bold text-white mb-3">
                Profile Incomplete
              </h2>
              <p className="text-gray-300 text-lg mb-6">
                Please complete your profile to see the subscription plans.
              </p>
              <Link
                to="/portal/account"
                className="inline-block bg-[#C9A55C] hover:bg-[#D4B06A] text-white px-8 py-3 rounded-xl font-semibold text-base transition"
              >
                Complete Profile
              </Link>
            </div>
          )}

          {/* Lawyers Plan */}
          {(userCategory === "Lawyer" || userCategory === "Advocate") && (
            <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white mb-8">
              <h2 className="text-3xl font-bold text-blue-400 text-center mb-12">
                Lawyers
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
                {/* Basic Card */}
                <div className={`bg-[#060125] rounded-2xl p-6 border-2 ${activeSubscriptionCategory === "Lawyer-Basic" ? "border-gray-400" : "border-white hover:border-yellow-500"} transition-all duration-300 flex flex-col relative`}>
                  {activeSubscriptionCategory === "Lawyer-Basic" && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-yellow-500 text-center mb-4">
                    {SubscriptionPlansConfig?.LAWYER_BASIC?.name}
                  </h3>
                  <div className="text-3xl font-bold text-white text-center mb-6">
                    {isINRCountry() ? SubscriptionPlansConfig.LAWYER_BASIC.priceDisplayINR : SubscriptionPlansConfig.LAWYER_BASIC.priceDisplayUSD}<span className="text-lg text-gray-400">{SubscriptionPlansConfig.LAWYER_BASIC.duration}</span>
                  </div>

                  <div className="space-y-3 flex-grow mb-6">
                    {SubscriptionPlansConfig.LAWYER_BASIC.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3">
                          <svg
                            className="w-4 h-4 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-white">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {activeSubscriptionCategory === "Lawyer-Basic" ? (
                    <div className="w-full bg-gray-500 text-white px-6 py-2.5 rounded-xl font-semibold text-base transition border border-gray-400 text-center">
                      Active
                    </div>
                  ) : (
                    <button
                      onClick={() => handleLawyerUpgrade("basic")}
                      disabled={paymentLoading}
                      className="w-full bg-[#C9A55C] hover:bg-[#D4B06A] text-white px-6 py-2.5 rounded-xl font-semibold text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {paymentLoading ? "Processing..." : "Upgrade"}
                    </button>
                  )}
                </div>

                {/* Premium Card - temporarily hidden */}
                {/* <div className={`bg-[#060125] rounded-2xl p-6 border-2 ${activeSubscriptionCategory === "Lawyer-Premium" ? "border-gray-400" : "border-white hover:border-yellow-500"} transition-all duration-300 flex flex-col transform md:scale-105 relative`}>
                  {activeSubscriptionCategory === "Lawyer-Premium" ? (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </div>
                  ) : (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 bg-yellow-500 text-[#050125] px-4 py-1 rounded-full text-sm font-bold">
                      POPULAR
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-yellow-500 text-center mb-4 mt-2">
                    {SubscriptionPlansConfig.LAWYER_PREMIUM.name}
                  </h3>
                  <div className="text-3xl font-bold text-white text-center mb-6">
                    {isINRCountry() ? SubscriptionPlansConfig.LAWYER_PREMIUM.priceDisplayINR : SubscriptionPlansConfig.LAWYER_PREMIUM.priceDisplayUSD}<span className="text-lg text-gray-400">{SubscriptionPlansConfig.LAWYER_PREMIUM.duration}</span>
                  </div>

                  <div className="space-y-3 flex-grow mb-6">
                    {SubscriptionPlansConfig.LAWYER_PREMIUM.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3">
                          <svg
                            className="w-4 h-4 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-white">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {activeSubscriptionCategory === "Lawyer-Premium" ? (
                    <div className="w-full bg-gray-500 text-white px-6 py-2.5 rounded-xl font-semibold text-base transition border border-gray-400 text-center">
                      Active
                    </div>
                  ) : (
                    <button
                      onClick={() => handleLawyerUpgrade("premium")}
                      disabled={paymentLoading}
                      className="w-full bg-[#C9A55C] hover:bg-[#D4B06A] text-white px-6 py-2.5 rounded-xl font-semibold text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {paymentLoading ? "Processing..." : "Upgrade"}
                    </button>
                  )}
                </div> */}

                {/* Enterprise Card */}
                <div className={`bg-[#060125] rounded-2xl p-6 border-2 ${activeSubscriptionCategory === "Lawyer-Enterprise" ? "border-gray-400" : "border-white hover:border-yellow-500"} transition-all duration-300 flex flex-col relative`}>
                  {activeSubscriptionCategory === "Lawyer-Enterprise" && (
                    <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                      CURRENT PLAN
                    </div>
                  )}
                  <h3 className="text-2xl font-bold text-yellow-500 text-center mb-4">
                    {SubscriptionPlansConfig.LAWYER_ENTERPRISE.name}
                  </h3>
                  <div className="text-3xl font-bold text-white text-center mb-6">
                    {SubscriptionPlansConfig.LAWYER_ENTERPRISE.priceDisplay}
                  </div>

                  <div className="space-y-3 flex-grow mb-6">
                    {SubscriptionPlansConfig.LAWYER_ENTERPRISE.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3">
                          <svg
                            className="w-4 h-4 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-white">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {activeSubscriptionCategory === "Lawyer-Enterprise" ? (
                    <div className="w-full bg-gray-500 text-white px-6 py-2.5 rounded-xl font-semibold text-base transition border border-gray-400 text-center">
                      Active
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowEnquiryModal(true)}
                      className="w-full bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-xl font-semibold text-base transition border border-yellow-500"
                    >
                      Inquire
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Law Student Paid Plan */}
          {userCategory === "Law student" && (
            <div className={`bg-[#1E1E45] rounded-2xl p-8 border-2 ${activeSubscriptionCategory?.startsWith("Student") ? "border-gray-400" : "border-white"} mb-8 relative`}>
              {activeSubscriptionCategory?.startsWith("Student") && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-gray-500 text-white px-3 py-1 rounded-full text-xs font-bold">
                  CURRENT PLAN
                </div>
              )}
              <h2 className="text-3xl font-bold text-green-500 text-center mb-4">
                Law Student
              </h2>

              <div className="text-4xl font-bold text-white text-center mb-4">
                {selectedPlan === 0
                  ? SubscriptionPlansConfig.LAW_STUDENT_SIX_MONTHS.priceDisplay
                  : SubscriptionPlansConfig.LAW_STUDENT_ONE_YEAR.priceDisplay}
              </div>

              {/* Plan Selection */}
              <div className="flex justify-center gap-4 mb-6">
                <button
                  onClick={() => setSelectedPlan(0)}
                  className={`px-6 py-2 rounded-full font-medium transition ${
                    selectedPlan === 0
                      ? "bg-yellow-500 text-[#050125]"
                      : "bg-black text-white border border-[#050125]"
                  }`}
                >
                  6 Months
                </button>
                <button
                  onClick={() => setSelectedPlan(1)}
                  className={`px-6 py-2 rounded-full font-medium transition ${
                    selectedPlan === 1
                      ? "bg-yellow-500 text-[#050125]"
                      : "bg-black text-white border border-[#050125]"
                  }`}
                >
                  Yearly
                </button>
              </div>

              <div className="space-y-3 mb-6">
                {(selectedPlan === 0
                  ? SubscriptionPlansConfig.LAW_STUDENT_SIX_MONTHS.features
                  : SubscriptionPlansConfig.LAW_STUDENT_ONE_YEAR.features
                ).map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3 mt-1 flex-shrink-0">
                      <svg
                        className="w-4 h-4 text-yellow-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-white text-lg">{feature}</span>
                  </div>
                ))}
              </div>

              {/* Show Active badge if student has paid subscription */}
              {activeSubscriptionCategory?.startsWith("Student") ? (
                <div className="flex justify-center">
                  <div className="bg-gray-500 text-white px-6 py-2.5 rounded-xl font-semibold text-base border border-gray-400">
                    Active
                  </div>
                </div>
              ) : (
                <>
                  {!hasActiveSubscription && (
                    <div className="flex justify-center">
                      <button
                        onClick={handleLawStudentUpgrade}
                        disabled={paymentLoading}
                        className="bg-[#050125] hover:bg-[#0a0247] text-white px-8 py-3 rounded-lg font-semibold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {paymentLoading ? "Processing..." : "Subscribe Now"}
                      </button>
                    </div>
                  )}

                  {hasActiveSubscription && subscriptionType === "free" && (
                    <div className="text-center">
                      <p className="text-yellow-500 mb-4">
                        Your free subscription will expire on{" "}
                        {subscriptionEndDate?.toLocaleDateString()}
                      </p>
                      <div className="flex justify-center">
                        <button
                          onClick={handleLawStudentUpgrade}
                          disabled={paymentLoading}
                          className="bg-[#050125] hover:bg-[#0a0247] text-white px-8 py-3 rounded-lg font-semibold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          {paymentLoading ? "Processing..." : "Subscribe Now"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* Client Custom Enquiry Plan */}
          {isClient && userCategory === "client" && (
            <div className="bg-[#1E1E45] rounded-2xl p-8 border-2 border-white mb-8">
              <h2 className="text-3xl font-bold text-blue-400 text-center mb-12">
                For Clients
              </h2>

              <div className="max-w-sm mx-auto">
                <div className="bg-[#060125] rounded-2xl p-6 border-2 border-white hover:border-yellow-500 transition-all duration-300 flex flex-col">
                  <h3 className="text-2xl font-bold text-yellow-500 text-center mb-4">
                    {SubscriptionPlansConfig.CLIENT_ENTERPRISE.name}
                  </h3>
                  <div className="text-3xl font-bold text-white text-center mb-6">
                    {SubscriptionPlansConfig.CLIENT_ENTERPRISE.priceDisplay}
                  </div>

                  <div className="space-y-3 flex-grow mb-6">
                    {SubscriptionPlansConfig.CLIENT_ENTERPRISE.features.map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3 flex-shrink-0">
                          <svg
                            className="w-4 h-4 text-yellow-500"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={3}
                              d="M5 13l4 4L19 7"
                            />
                          </svg>
                        </div>
                        <span className="text-white">{feature}</span>
                      </div>
                    ))}
                  </div>

                  <button
                    onClick={() => setShowEnquiryModal(true)}
                    className="w-full bg-gray-700 hover:bg-gray-600 text-white px-6 py-2.5 rounded-xl font-semibold text-base transition border border-yellow-500"
                  >
                    Inquire
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Expired Subscription Message */}
          {!hasActiveSubscription && subscriptionEndDate && (
            <div className="bg-red-500 bg-opacity-10 border-2 border-red-500 rounded-2xl p-6 text-center">
              <h3 className="text-2xl font-bold text-red-500 mb-2">
                Subscription Expired
              </h3>
              <p className="text-white text-lg mb-4">
                Your subscription expired on{" "}
                {subscriptionEndDate?.toLocaleDateString()}
              </p>
              {userCategory === "Law student" && (
                <div className="flex justify-center">
                  <button
                    onClick={handleLawStudentUpgrade}
                    disabled={paymentLoading}
                    className="bg-[#050125] hover:bg-[#0a0247] text-white px-8 py-3 rounded-lg font-semibold text-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {paymentLoading ? "Processing..." : "Subscribe Now"}
                  </button>
                </div>
              )}
            </div>
          )}

        </div>

        {/* Enquiry Modal */}
        {showEnquiryModal && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4">
            <div className="bg-[#050125] rounded-3xl border-2 border-white max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="relative bg-[#050125] pt-6 pb-4 px-6 border-b border-gray-700">
                <button
                  onClick={() => setShowEnquiryModal(false)}
                  className="absolute top-3 right-4 text-white hover:text-gray-400 text-2xl font-light"
                >
                  &times;
                </button>
                <h2 className="text-2xl font-bold text-white text-center">
                  Send Your Enquiry
                </h2>
              </div>

              {/* Form */}
              <form onSubmit={handleEnquirySubmit} className="p-6 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={enquiryForm.name}
                      onChange={handleEnquiryChange}
                      placeholder="Your Name"
                      className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={enquiryForm.email}
                      onChange={handleEnquiryChange}
                      placeholder="Your Email"
                      className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                      Contact No.
                    </label>
                    <input
                      type="tel"
                      name="contactNo"
                      value={enquiryForm.contactNo}
                      onChange={handleEnquiryChange}
                      placeholder="10 digit phone number"
                      maxLength={10}
                      className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                      Representing Firm
                    </label>
                    <SelectMenu
                      name="representingFirm"
                      value={enquiryForm.representingFirm}
                      onChange={handleEnquiryChange}
                      className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none appearance-none cursor-pointer text-sm transition"
                      style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                        backgroundRepeat: 'no-repeat',
                        backgroundPosition: 'right 0.75rem center',
                        backgroundSize: '1.2em'
                      }}
                    >
                      <option value="No" className="bg-[#050125]">No</option>
                      <option value="Yes" className="bg-[#050125]">Yes</option>
                    </SelectMenu>
                  </div>

                  {enquiryForm.representingFirm === "Yes" && (
                    <div className="md:col-span-2">
                      <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                        Firm Name
                      </label>
                      <input
                        type="text"
                        name="firmName"
                        value={enquiryForm.firmName}
                        onChange={handleEnquiryChange}
                        placeholder="Your Firm Name"
                        className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                        required={enquiryForm.representingFirm === "Yes"}
                      />
                    </div>
                  )}

                  {isClient && (
                    <div>
                      <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                        Approximate number of cases
                      </label>
                      <input
                        type="number"
                        name="approximateCases"
                        value={enquiryForm.approximateCases}
                        onChange={handleEnquiryChange}
                        placeholder="e.g. 50"
                        className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                      />
                    </div>
                  )}

                  {isClient && (
                    <div>
                      <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                        Court with maximum cases
                      </label>
                      <input
                        type="text"
                        name="maxCaseCourt"
                        value={enquiryForm.maxCaseCourt}
                        onChange={handleEnquiryChange}
                        placeholder="e.g. Supreme Court"
                        className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm transition"
                      />
                    </div>
                  )}
                </div>

                <div>
                  <label className="block text-gray-400 mb-1.5 text-xs font-medium">
                    Requirements
                  </label>
                  <textarea
                    name="requirements"
                    value={enquiryForm.requirements}
                    onChange={handleEnquiryChange}
                    rows="3"
                    placeholder="Tell us about your requirements..."
                    className="w-full px-3 py-2.5 rounded-xl bg-transparent text-white border-2 border-white focus:border-yellow-500 focus:outline-none placeholder-gray-500 text-sm resize-none transition"
                  />
                </div>

                <div className="flex justify-center mt-4">
                  <button
                    type="submit"
                    disabled={enquiryLoading}
                    className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-2 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  >
                    {enquiryLoading && <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />}
                    {enquiryLoading ? "Submitting..." : "Submit"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Free Plan Features Modal */}
        {showFreePlanModal && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-[9999] p-4">
            <div className="bg-[#050125] rounded-3xl border-2 border-white max-w-md w-full">
              {/* Header */}
              <div className="relative pt-6 pb-4 px-6">
                <button
                  onClick={() => setShowFreePlanModal(false)}
                  className="absolute top-3 right-4 text-white hover:text-gray-400 text-2xl font-light"
                >
                  &times;
                </button>
                <h2 className="text-2xl font-bold text-white text-center">
                  Free Plan
                </h2>
              </div>

              {/* Features */}
              <div className="p-6 pt-2 space-y-3">
                {(isClient ? SubscriptionPlansConfig.CLIENT_FREE_PLAN : SubscriptionPlansConfig.FREE_PLAN).features.map((feature, index) => (
                  <div key={index} className="flex items-start">
                    <div className="w-6 h-6 rounded-full border-2 border-yellow-500 flex items-center justify-center mr-3 mt-0.5 flex-shrink-0">
                      <svg
                        className="w-4 h-4 text-yellow-500"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={3}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                    </div>
                    <span className="text-white text-base">{feature}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

      </div>
      <Footer />
    </>
  );
}

export default Subscription;